import { ChevronUp, FileClock, LogOut, MessageSquareText, Plus, Settings, ShieldCheck, Trash2, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { deleteAccount, fetchSessions } from '../lib/api';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import type { SessionSummary } from '../types';
import WaveIcon from './WaveIcon';

function SessionIcon({ mode }: { mode: SessionSummary['mode'] }) {
  if (mode === 'text') return <MessageSquareText size={16} />;
  if (mode === 'voice') return <WaveIcon size={16} />;
  return <UserRound size={16} />;
}

function SessionLink({ session, active, compact = false }: { session: SessionSummary; active: boolean; compact?: boolean }) {
  return (
    <Link href={`/sessions/${session.id}`} className={`${active ? 'active' : ''} ${compact ? 'compact' : ''}`} aria-current={active ? 'page' : undefined}>
      <span className={`session-nav-icon ${session.mode}`}><SessionIcon mode={session.mode} /></span>
      <span><strong>{session.interviewType}</strong><small>{session.role}{session.score != null ? ` · ${session.score}` : ''}</small></span>
    </Link>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, signOut, getToken, configured } = useAuth();
  const [location] = useLocation();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [sessionsError, setSessionsError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const openedEventId = useRef(crypto.randomUUID());

  useEffect(() => {
    void trackJourney(getToken, 'app_opened', { clientEventId: openedEventId.current, properties: { path: location } });
  }, [getToken]);

  useEffect(() => {
    void getToken().then(fetchSessions).then(({ sessions: loaded }) => { setSessions(loaded); setSessionsError(''); })
      .catch(() => setSessionsError('Could not load session logs.'));
  }, [getToken, location]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setSettingsOpen(false);
      setAccountSettingsOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, []);

  async function handleDeleteAccount() {
    if (!window.confirm('Delete your RoundJump account and all interview data? This cannot be undone.')) return;
    try {
      const token = await getToken();
      await deleteAccount(token);
      await signOut();
    } catch {
      window.alert('Account deletion could not be queued. Please try again.');
    }
  }

  function settingsMenu(className: string) {
    if (!settingsOpen) return null;
    return (
      <div className={`settings-menu ${className}`} role="menu">
        {user?.isAdmin && <Link href="/admin" role="menuitem" onClick={() => setSettingsOpen(false)}><ShieldCheck size={16} /> Admin</Link>}
        <button role="menuitem" onClick={() => { setSettingsOpen(false); setAccountSettingsOpen(true); }}><Settings size={16} /> Settings</button>
        <a href="/privacy-policy" role="menuitem"><FileClock size={16} /> Privacy policy</a>
        {configured && <button role="menuitem" onClick={() => void signOut()}><LogOut size={16} /> Sign out</button>}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <Link href="/" className="brand"><img className="brand-mark" src="/brand/roundjump-logo.webp" alt="" /><span>RoundJump</span></Link>
        <Link href="/" className={`new-practice-link ${location === '/' ? 'active' : ''}`}><Plus size={18} /><span>New practice</span></Link>
        <div className="session-nav-heading"><span>Session logs</span></div>
        <nav className="session-nav" aria-label="Session logs">
          {sessions.map((session) => <SessionLink key={session.id} session={session} active={location === `/sessions/${session.id}`} />)}
          {!sessions.length && sessionsError && <p>{sessionsError}</p>}
        </nav>
        <div className="sidebar-foot settings-wrap">
          {settingsMenu('desktop-settings-menu')}
          <button className="user-row user-settings-trigger" aria-haspopup="menu" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((open) => !open)}>
            <span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span>
            <span className="user-copy"><strong>{user?.name}</strong><small>{user?.email}</small></span>
            <ChevronUp className={settingsOpen ? 'open' : ''} size={17} />
          </button>
        </div>
      </aside>
      <div className="content-shell">
        <header className="mobile-header">
          <Link href="/" className="brand"><img className="brand-mark" src="/brand/roundjump-logo.webp" alt="" /><span>RoundJump</span></Link>
          <div className="mobile-settings-wrap">
            <button className="mobile-user-trigger" aria-label="Open settings" aria-haspopup="menu" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((open) => !open)}><span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span></button>
            {settingsMenu('mobile-settings-menu')}
          </div>
        </header>
        <nav className="mobile-log-nav" aria-label="Practice and session logs">
          <Link href="/" className={location === '/' ? 'active compact' : 'compact'}><Plus size={16} /><span><strong>New practice</strong></span></Link>
          {sessions.slice(0, 10).map((session) => <SessionLink key={session.id} session={session} active={location === `/sessions/${session.id}`} compact />)}
        </nav>
        <main className="page">{children}</main>
      </div>
      {accountSettingsOpen && <div className="account-settings-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setAccountSettingsOpen(false); }}>
        <section className="account-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="account-settings-title">
          <button className="account-settings-close" type="button" aria-label="Close settings" onClick={() => setAccountSettingsOpen(false)}><X size={19} /></button>
          <h2 id="account-settings-title">Settings</h2>
          <div className="account-settings-user">
            <span className="user-avatar">{user?.name.charAt(0).toUpperCase()}</span>
            <span><strong>{user?.name}</strong><small>{user?.email}</small></span>
          </div>
          {configured && <section className="account-settings-danger">
            <div><strong>Delete account</strong><p>Permanently delete your account and interview data.</p></div>
            <button type="button" onClick={() => void handleDeleteAccount()}><Trash2 size={16} /> Delete account</button>
          </section>}
        </section>
      </div>}
    </div>
  );
}
